import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/utils/cn';

export type BadgeVariant =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

const VARIANTS: Record<BadgeVariant, string> = {
  neutral: 'bg-surface-sunken text-fg-secondary border-border',
  brand: 'bg-brand/10 text-brand border-brand/20',
  success: 'bg-success-subtle text-success-fg border-success/20',
  warning: 'bg-warning-subtle text-warning-fg border-warning/20',
  danger: 'bg-danger-subtle text-danger-fg border-danger/20',
  info: 'bg-info-subtle text-info-fg border-info/20',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /**
   * Status must never be carried by colour alone — a red badge and a green badge are
   * the same badge to a colourblind treasurer, and both are grey when printed.
   */
  icon?: ReactNode;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'neutral', icon, className, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-caption font-medium whitespace-nowrap',
        VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </span>
  ),
);

Badge.displayName = 'Badge';
